import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  IceCream,
  Tag,
  LogOut,
  CheckCircle2,
  XCircle,
  Trash2,
  Plus,
  UserRound,
  ClipboardList,
} from "lucide-react";
import { motion } from "framer-motion";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "flavours", label: "Flavours", icon: IceCream },
  { key: "menu", label: "Menu Items", icon: IceCream },
  { key: "coupons", label: "Coupons", icon: Tag },
  { key: "catering", label: "Catering", icon: ClipboardList },
  { key: "founder", label: "Founder", icon: UserRound },
  { key: "feedback", label: "Feedback", icon: ClipboardList },
 
];
// =========================================================
// CLOUDINARY IMAGE UPLOAD
// =========================================================

async function uploadImageToCloudinary(file) {
  if (!file) return "";

  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await api.post(
      "/upload-image",
      formData
    );

    return response.data.url;
  } catch (error) {
    console.error(
      "Cloudinary upload failed:",
      error
    );

    throw error;
  }
}
export default function AdminPanel() {
  const [token, setToken] = useState(
    localStorage.getItem("icp_admin_token") || ""
  );
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("dashboard");
  const [feedback, setFeedback] = useState([]);

  const loadFeedback = async () => {
  try {
    const response = await api.get("/feedback");
    setFeedback(response.data || []);
  } catch (error) {
    console.error("Failed to load feedback:", error);
    toast.error("Failed to load feedback");
  }
};
 useEffect(() => {
    if (token && tab === "feedback") {
      loadFeedback();
    }
  }, [token, tab]);


  const login = async () => {
    try {
      const r = await api.post("/auth/admin", { password });

      localStorage.setItem("icp_admin_token", r.data.token);
      setToken(r.data.token);

      toast.success("Welcome, Admin");
    } catch (error) {
      console.error("Admin login error:", error);
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    localStorage.removeItem("icp_admin_token");
    setToken("");
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto py-24 px-6">
        <div className="glass rounded-3xl p-8">
          <h1 className="font-heading text-4xl mb-2">
            Admin Login
          </h1>

          <p className="text-sm text-[#B7D8D5] mb-6">
            Enter admin password
          </p>

          <input
            type="password"
            data-testid="admin-password"
            style={{ color: "#1F5F5C" }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && login()
            }
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] bg-white px-6 py-3 mb-4 outline-none focus:ring-2 focus:ring-[#B8E14A]"
          />

          <button
            data-testid="admin-login"
            onClick={login}
            className="btn-primary w-full justify-center"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B8E14A]">
            Admin Panel
          </div>

          <h1 className="font-heading text-4xl">
            Ice Cream Paradise Control Room
          </h1>
        </div>

        <button
          data-testid="admin-logout"
          onClick={logout}
          className="btn-outline text-sm"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* TABS */}
      <div className="flex gap-2 overflow-x-auto mb-8 pb-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full text-sm transition ${
              tab === t.key
                ? "bg-[#B8E14A] text-[#1F5F5C]"
                : "bg-white text-[#1F5F5C] border border-[rgba(184,225,74,0.35)]"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB CONTENT */}
      {tab === "dashboard" && <DashboardTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "flavours" && <FlavoursTab />}
      {tab === "menu" && <MenuTab />}
      {tab === "coupons" && <CouponsTab />}
      {tab === "catering" && <CateringTab />}
      {tab === "feedback" && <FeedbackTab feedback={feedback}/>}
      {tab === "founder" && <FounderTab />}
      
    </div>
  );
}


/* =========================================================
   STAT CARD
========================================================= */

function StatCard({ label, value, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-3xl p-6"
    >
      <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
        {label}
      </div>

      <div
        className={`font-heading text-4xl mt-2 ${
          accent ? "text-[#B8E14A]" : ""
        }`}
      >
        {value}
      </div>
    </motion.div>
  );
}


/* =========================================================
   DASHBOARD
========================================================= */

function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadStats = async () => {
      try {
        setError("");

        const response = await api.get("/admin/stats");

        console.log(
          "Admin stats response:",
          response.data
        );

        if (!mounted) return;

        const data = response.data || {};

        setStats({
          today_sales: data.today_sales ?? 0,
          total_revenue: data.total_revenue ?? 0,
          total_orders: data.total_orders ?? 0,
          pending_orders: data.pending_orders ?? 0,
          completed_orders: data.completed_orders ?? 0,
          today_orders: data.today_orders ?? 0,
          unique_customers: data.unique_customers ?? 0,

          /*
           * IMPORTANT FIX
           *
           * The old code directly used:
           *
           * stats.popular_flavours.length
           *
           * If the backend doesn't return popular_flavours,
           * React crashes.
           *
           * This guarantees it is always an array.
           */
          popular_flavours: Array.isArray(
            data.popular_flavours
          )
            ? data.popular_flavours
            : [],
        });
      } catch (err) {
        console.error(
          "Failed to load admin statistics:",
          err
        );

        if (!mounted) return;

        setError(
          err?.response?.data?.detail ||
            "Failed to load dashboard statistics."
        );

        /*
         * Keep dashboard usable even when the API
         * returns an incomplete response.
         */
        setStats({
          today_sales: 0,
          total_revenue: 0,
          total_orders: 0,
          pending_orders: 0,
          completed_orders: 0,
          today_orders: 0,
          unique_customers: 0,
          popular_flavours: [],
        });
      }
    };

    loadStats();

    return () => {
      mounted = false;
    };
  }, []);

  if (!stats) {
    return (
      <div className="text-[#B7D8D5]">
        Loading dashboard...
      </div>
    );
  }

  return (
    <div>

      {error && (
        <div className="mb-6 rounded-2xl border border-red-300 bg-red-100 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* STATISTICS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">

        <StatCard
          label="Today's Sales"
          value={`₹${stats.today_sales}`}
          accent
        />

        <StatCard
          label="Total Revenue"
          value={`₹${stats.total_revenue}`}
        />

        <StatCard
          label="Total Orders"
          value={stats.total_orders}
        />

        <StatCard
          label="Pending"
          value={stats.pending_orders}
        />

        <StatCard
          label="Completed"
          value={stats.completed_orders}
        />

        <StatCard
          label="Today's Orders"
          value={stats.today_orders}
        />

        <StatCard
          label="Unique Customers"
          value={stats.unique_customers}
        />

      </div>

      {/* POPULAR FLAVOURS */}
      <div className="glass rounded-3xl p-6 mt-6">

        <div className="text-xs uppercase tracking-widest text-[#B7D8D5] mb-4">
          Popular Flavours
        </div>

        <div className="space-y-2">

          {stats.popular_flavours.length === 0 ? (
            <div className="text-[#B7D8D5] text-sm">
              No orders yet.
            </div>
          ) : (
            stats.popular_flavours.map(
              (item, index) => (
                <div
                  key={
                    item?.name ||
                    `flavour-${index}`
                  }
                  className="flex justify-between"
                >
                  <span className="font-medium">
                    {item?.name || "Unknown"}
                  </span>

                  <span className="text-[#B8E14A]">
                    {item?.count ?? 0}
                  </span>
                </div>
              )
            )
          )}

        </div>
      </div>
    </div>
  );
}


/* =========================================================
   ORDERS
========================================================= */

function OrdersTab() {
  const [orders, setOrders] = useState([]);

  const refresh = async () => {
    try {
      const response = await api.get("/orders");

      setOrders(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error("Failed to load orders:", error);
      toast.error("Failed to load orders");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(
        `/orders/${id}/status`,
        { status }
      );

      toast.success("Order status updated");
      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update order");
    }
  };

  const verify = async (id, status) => {
    try {
      await api.patch(
        `/orders/${id}/verify-payment`,
        {
          payment_status: status,
        }
      );

      toast.success(
        status === "verified"
          ? "Payment verified"
          : "Payment rejected"
      );

      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update payment");
    }
  };

  return (
    <div className="space-y-4">

      {orders.map((o) => (
        <div
          key={o.id}
          className="glass rounded-3xl p-6"
          data-testid={`admin-order-${o.order_code}`}
        >

          <div className="flex flex-wrap items-start justify-between gap-4">

            <div>

              <div className="font-heading text-xl">
                {o.order_code}
              </div>

              <div className="text-xs text-[#B7D8D5]">
                {o.created_at
                  ? new Date(
                      o.created_at
                    ).toLocaleString()
                  : ""}
                {" · "}
                {o.delivery?.name || ""}
                {" · "}
                {o.delivery?.phone || ""}
              </div>

              <div className="text-xs text-[#E6F4F2] mt-1">
                {o.delivery?.address || "Address unavailable"}
              </div>

              {/* GPS INFORMATION */}
              {(o.delivery?.latitude != null ||
                o.delivery?.longitude != null) && (
                <div className="text-xs text-[#B7D8D5] mt-1">
                  GPS:{" "}
                  {o.delivery?.latitude ?? "—"},{" "}
                  {o.delivery?.longitude ?? "—"}
                </div>
              )}

              {o.delivery?.shop_name && (
                <div className="text-xs text-[#B8E14A] mt-1">
                  Kitchen: {o.delivery.shop_name}
                </div>
              )}

            </div>

            <div className="text-right">

              <div className="font-heading text-2xl text-[#B8E14A]">
                ₹{o.total ?? 0}
              </div>

              <div className="text-xs uppercase tracking-widest text-[#B7D8D5] mt-1">
                {String(
                  o.status || ""
                ).replace(/_/g, " ")}
              </div>

            </div>

          </div>

          {/* ITEMS */}
          <div className="mt-3 text-sm">
            {(Array.isArray(o.items)
              ? o.items
              : []
            ).map((item, index) => (
              <span
                key={index}
                className="mr-3"
              >
                {item?.name || "Item"} ×
                {item?.quantity ?? 0}
              </span>
            ))}
          </div>

          {/* PAYMENT */}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">

            <span className="px-2 py-1 rounded-full bg-white/60 border">
              {o.payment_method || "Unknown"}
            </span>

            <span
              className={`px-2 py-1 rounded-full border ${
                o.payment_status ===
                "verified"
                  ? "bg-green-100 border-green-300"
                  : o.payment_status ===
                    "rejected"
                  ? "bg-red-100 border-red-300"
                  : "bg-yellow-100 border-yellow-300"
              }`}
            >
              {o.payment_status ||
                "pending"}
            </span>

            {o.payment_screenshot && (
              <a
                href={o.payment_screenshot}
                target="_blank"
                rel="noreferrer"
                className="text-[#B8E14A] underline"
              >
                View screenshot
              </a>
            )}

          </div>

          {/* STATUS BUTTONS */}
          <div className="mt-4 flex flex-wrap gap-2">

            {[
              "accepted",
              "preparing",
              "ready",
              "out_for_delivery",
              "delivered",
              "rejected",
            ].map((status) => (
              <button
                key={status}
                onClick={() =>
                  setStatus(
                    o.id,
                    status
                  )
                }
                data-testid={`admin-set-${o.order_code}-${status}`}
                className="text-xs px-3 py-1.5 rounded-full border border-[rgba(184,225,74,0.35)] hover:bg-[#1F5F5C] hover:text-white transition"
              >
                {status.replace(
                  /_/g,
                  " "
                )}
              </button>
            ))}

            {o.payment_method ===
              "QR" &&
              o.payment_status ===
                "pending" && (
                <>
                  <button
                    onClick={() =>
                      verify(
                        o.id,
                        "verified"
                      )
                    }
                    data-testid={`admin-verify-${o.order_code}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-green-600 text-white flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Verify Payment
                  </button>

                  <button
                    onClick={() =>
                      verify(
                        o.id,
                        "rejected"
                      )
                    }
                    className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white flex items-center gap-1"
                  >
                    <XCircle className="w-3 h-3" />
                    Reject Payment
                  </button>
                </>
              )}

          </div>

        </div>
      ))}

      {orders.length === 0 && (
        <div className="text-[#B7D8D5] text-center py-12">
          No orders yet.
        </div>
      )}

    </div>
  );
}
/* =========================================================
   CATERING
========================================================= */
function CateringTab() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setLoading(true);

      const response = await api.get("/admin/catering");

      setEnquiries(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to load catering enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (loading) {
    return (
      <div className="text-[#B7D8D5]">
        Loading catering enquiries...
      </div>
    );
  }

  return (
    <div className="space-y-4">

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-3xl">
            Catering Requests
          </h2>

          <p className="text-sm text-[#B7D8D5] mt-1">
            Manage enquiries and quotations.
          </p>
        </div>

        <button
          onClick={refresh}
          className="btn-outline text-sm"
        >
          Refresh
        </button>
      </div>

      {enquiries.length === 0 ? (
        <div className="glass rounded-3xl p-8 text-center text-[#B7D8D5]">
          No catering enquiries yet.
        </div>
      ) : (
        enquiries.map((enquiry) => (
          <CateringEnquiryCard
            key={enquiry.id}
            enquiry={enquiry}
            onUpdate={refresh}
          />
        ))
      )}

    </div>
  );
}

function CateringEnquiryCard({
  enquiry,
  onUpdate,
}) {
  const [editing, setEditing] = useState(false);

  const [form, setForm] = useState({
  ice_cream_amount:
    enquiry.quotation?.ice_cream_amount || 0,

  staff_amount:
    enquiry.staff_required === "Yes"
      ? enquiry.quotation?.staff_amount || 0
      : 0,

  transport_amount:
    enquiry.service_type === "Delivery"
      ? enquiry.quotation?.transport_amount || 0
      : 0,

  advance_amount:
    enquiry.quotation?.advance_amount || 0,

  notes:
    enquiry.quotation?.notes || "",
});

  const total =
  Number(form.ice_cream_amount || 0) +
  (enquiry.staff_required === "Yes"
    ? Number(form.staff_amount || 0)
    : 0) +
  (enquiry.service_type === "Delivery"
    ? Number(form.transport_amount || 0)
    : 0);

  const balance =
    total - Number(form.advance_amount || 0);

  const saveQuotation = async () => {
  const iceCreamAmount =
    Number(form.ice_cream_amount || 0);

  const staffAmount =
    enquiry.staff_required === "Yes"
      ? Number(form.staff_amount || 0)
      : 0;

  const transportAmount =
    enquiry.service_type === "Delivery"
      ? Number(form.transport_amount || 0)
      : 0;

  const advanceAmount =
    Number(form.advance_amount || 0);

  const quotationTotal =
    iceCreamAmount +
    staffAmount +
    transportAmount;

  if (advanceAmount > quotationTotal) {
    toast.error(
      "Advance cannot be greater than total"
    );
    return;
  }

  try {
    await api.post(
      `/admin/catering/${enquiry.id}/quotation`,
      {
        ice_cream_amount: iceCreamAmount,

        // Automatically zero if customer doesn't need staff
        staff_amount: staffAmount,

        // Automatically zero if customer selected pickup
        transport_amount: transportAmount,

        // No other charges
        other_amount: 0,

        advance_amount: advanceAmount,

        notes: form.notes,
      }
    );

    toast.success(
      "Quotation saved successfully"
    );

    setEditing(false);
    onUpdate();

  } catch (error) {
    console.error(error);

    toast.error(
      error?.response?.data?.detail ||
        "Failed to save quotation"
    );
  }
};
  
  const assignKitchen = async (kitchen_id) => {
    if (!kitchen_id) return;

    try {
      await api.patch(
        `/admin/catering/${enquiry.id}/kitchen`,
        { kitchen_id }
      );

      toast.success("Catering kitchen assigned");
      onUpdate();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail ||
          "Failed to assign kitchen"
      );
    }
  };

  const assignedKitchenLabel =
    enquiry.kitchen_name ||
    (enquiry.kitchen_id === "kitchen_1"
      ? "Kitchen 1"
      : enquiry.kitchen_id === "kitchen_2"
      ? "Kitchen 2"
      : "");

  return (
    <div className="glass rounded-3xl p-6">

      {/* CUSTOMER */}
      <div className="flex flex-wrap items-start justify-between gap-4">

        <div>
          <div className="font-heading text-2xl">
            {enquiry.name}
          </div>

          <div className="text-sm text-[#B7D8D5] mt-1">
            {enquiry.phone}
            {" · "}
            {enquiry.email || "No email"}
          </div>
        </div>

        <div className="text-right">

          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Status
          </div>

          <div className="text-[#B8E14A] font-medium mt-1">
            {String(
              enquiry.status || "new"
            ).replace(/_/g, " ")}
          </div>

        </div>

      </div>

      {/* EVENT DETAILS */}
      <div className="grid md:grid-cols-2 gap-4 mt-5">

        <div>
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Event
          </div>

          <div className="mt-1">
            {enquiry.event_type}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Guests
          </div>

          <div className="mt-1">
            {enquiry.guests}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Date
          </div>

          <div className="mt-1">
            {enquiry.event_date}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Time
          </div>

          <div className="mt-1">
            {enquiry.event_time ||
              "Not specified"}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Location
          </div>

          <div className="mt-1">
            {enquiry.location}
          </div>
        </div>
        {(enquiry.latitude != null || enquiry.longitude != null) && (
          <div className="md:col-span-2 rounded-2xl bg-white/30 p-3 text-xs">
            <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Event GPS
            </div>
            <div className="mt-1">
              {enquiry.latitude ?? "—"}, {enquiry.longitude ?? "—"}
              {enquiry.accuracy != null
                ? ` · accuracy ${Math.round(enquiry.accuracy)} m`
                : ""}
            </div>
            {enquiry.pincode && (
              <div className="mt-1">
                Pincode: {enquiry.pincode}
              </div>
            )}
            {enquiry.map_link && (
              <a
                href={enquiry.map_link}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-1 text-[#1F5F5C] underline"
              >
                Open event location in Google Maps
              </a>
            )}
          </div>
        )}

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Requirements
          </div>

          <div className="mt-1">
            {enquiry.requirements ||
              "No special requirements"}
          </div>
        </div>

      </div>

      {/* KITCHEN ASSIGNMENT */}
      <div className="mt-6 pt-5 border-t border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-xl">
              Catering Kitchen
            </h3>
            <div className="text-xs text-[#B7D8D5] mt-1">
              Assign a kitchen only after the catering advance is verified.
            </div>
          </div>

          {enquiry.payment_status === "verified" ? (
            <select
              value={enquiry.kitchen_id || ""}
              onChange={(e) => assignKitchen(e.target.value)}
              className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
            >
              <option value="">Select kitchen</option>
              <option value="kitchen_1">
                Kitchen 1 — Shirur Park
              </option>
              <option value="kitchen_2">
                Kitchen 2 — Keshwapur
              </option>
            </select>
          ) : (
            <span className="px-3 py-1.5 rounded-full bg-yellow-100 border border-yellow-300 text-xs text-yellow-800">
              Waiting for verified advance payment
            </span>
          )}
        </div>

        {assignedKitchenLabel && (
          <div className="mt-3 rounded-2xl bg-white/40 p-3 text-sm">
            <strong>Assigned:</strong> {assignedKitchenLabel}
            {enquiry.shop_name ? ` — ${enquiry.shop_name}` : ""}
          </div>
        )}

        {enquiry.payment_status === "verified" &&
          !enquiry.kitchen_id && (
            <div className="mt-3 text-xs text-orange-700">
              This catering request is paid but not yet assigned to a kitchen.
            </div>
          )}
      </div>

      {/* QUOTATION */}
      <div className="mt-6 pt-5 border-t border-white/10">

        <div className="flex items-center justify-between mb-4">

          <div>
            <h3 className="font-heading text-xl">
              Quotation
            </h3>

            <div className="text-xs text-[#B7D8D5]">
              {enquiry.quotation
                ? "Quotation created"
                : "No quotation yet"}
            </div>
          </div>

          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="btn-primary text-sm"
            >
              {enquiry.quotation
                ? "Edit Quotation"
                : "Create Quotation"}
            </button>
          )}

        </div>

        {editing ? (

          <div className="space-y-4">

          <div className="grid md:grid-cols-2 gap-3">

              {/* ICE CREAM AMOUNT */}
              <input
                type="number"
                min="0"
                placeholder="Ice cream amount"
                value={form.ice_cream_amount}
                onChange={(e) =>
                  setForm({
                    ...form,
                    ice_cream_amount: e.target.value,
                  })
                }
                className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
              />

              {/* STAFF AMOUNT */}
              <input
                type="number"
                min="0"
                placeholder={
                  enquiry.staff_required === "Yes"
                    ? "Staff amount"
                    : "Staff amount — Not Required"
                }
                value={
                  enquiry.staff_required === "Yes"
                    ? form.staff_amount
                    : 0
                }
                disabled={enquiry.staff_required !== "Yes"}
                onChange={(e) =>
                  setForm({
                    ...form,
                    staff_amount: e.target.value,
                  })
                }
                className={`rounded-full border px-4 py-2 text-[#1F5F5C] ${
                  enquiry.staff_required === "Yes"
                    ? "border-[rgba(184,225,74,0.35)] bg-white"
                    : "border-gray-300 bg-gray-200 text-gray-500"
                }`}
              />

              {/* TRANSPORT / DELIVERY AMOUNT */}
              <input
                type="number"
                min="0"
                placeholder={
                  enquiry.service_type === "Delivery"
                    ? "Transport / Delivery amount"
                    : "Transport amount — Pickup"
                }
                value={
                  enquiry.service_type === "Delivery"
                    ? form.transport_amount
                    : 0
                }
                disabled={enquiry.service_type !== "Delivery"}
                onChange={(e) =>
                  setForm({
                    ...form,
                    transport_amount: e.target.value,
                  })
                }
                className={`rounded-full border px-4 py-2 text-[#1F5F5C] ${
                  enquiry.service_type === "Delivery"
                    ? "border-[rgba(184,225,74,0.35)] bg-white"
                    : "border-gray-300 bg-gray-200 text-gray-500"
                }`}
              />

              {/* ADVANCE AMOUNT */}
              <input
                type="number"
                min="0"
                placeholder="Advance amount"
                value={form.advance_amount}
                onChange={(e) =>
                  setForm({
                    ...form,
                    advance_amount: e.target.value,
                  })
                }
                className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
              />

              {/* TOTAL */}
              <input
                value={`Total: ₹${total}`}
                readOnly
                className="rounded-full border px-4 py-2 bg-gray-100 text-[#1F5F5C]"
              />

            </div>

            {/* CUSTOMER SERVICE SUMMARY */}
            <div className="mt-4 rounded-2xl bg-white/40 p-4 text-sm">

              <div className="font-semibold text-[#1F5F5C] mb-3">
                Customer Service Selection
              </div>

              <div className="grid md:grid-cols-2 gap-2 text-[#1F5F5C]">

                <div>
                  Catering:
                  <strong className="ml-2">
                    {enquiry.service_type || "Not specified"}
                  </strong>
                </div>

                <div>
                  Staff:
                  <strong className="ml-2">
                    {enquiry.staff_required || "Not specified"}
                  </strong>
                </div>

              </div>

            </div>

            {/* QUOTATION NOTES */}
            <textarea
              placeholder="Quotation notes"
              value={form.notes}
              onChange={(e) =>
                setForm({
                  ...form,
                  notes: e.target.value,
                })
              }
              rows={3}
              className="w-full rounded-2xl border border-[rgba(184,225,74,0.35)] px-4 py-3 text-[#1F5F5C] bg-white"
            />

            {/* TOTAL SUMMARY */}
            <div className="rounded-2xl bg-white/50 p-4">

              <div className="flex justify-between text-sm">
                <span>Total</span>

                <strong>
                  ₹{total}
                </strong>
              </div>

              <div className="flex justify-between text-sm mt-2">
                <span>Advance</span>

                <strong>
                  ₹{Number(form.advance_amount || 0)}
                </strong>
              </div>

              <div className="flex justify-between text-lg mt-3 pt-3 border-t">

                <span>
                  Balance
                </span>

                <strong className="text-[#B8E14A]">
                  ₹{balance}
                </strong>

              </div>

            </div>

            <div className="flex gap-2">

              <button
                onClick={saveQuotation}
                className="btn-primary"
              >
                Save Quotation
              </button>

              <button
                onClick={() => setEditing(false)}
                className="btn-outline"
              >
                Cancel
              </button>

            </div>

          </div>

        ) : enquiry.quotation ? (

          <div className="rounded-2xl bg-white/40 p-4">

            <div className="grid sm:grid-cols-2 gap-3 text-sm">

              <div>
                Ice Cream:
                <strong className="ml-2">
                  ₹{enquiry.quotation.ice_cream_amount}
                </strong>
              </div>

              <div>
                Staff:
                <strong className="ml-2">
                  ₹{enquiry.quotation.staff_amount}
                </strong>
              </div>

              <div>
                Transport:
                <strong className="ml-2">
                  ₹{enquiry.quotation.transport_amount}
                </strong>
              </div>

              <div>
                Other:
                <strong className="ml-2">
                  ₹{enquiry.quotation.other_amount}
                </strong>
              </div>

            </div>

            <div className="mt-4 pt-4 border-t">

              <div className="flex justify-between">
                <span>Total</span>

                <strong>
                  ₹{enquiry.quotation.total_amount}
                </strong>
              </div>

              <div className="flex justify-between mt-2">
                <span>Advance</span>

                <strong>
                  ₹{enquiry.quotation.advance_amount}
                </strong>
              </div>

              <div className="flex justify-between mt-2 text-lg">
                <span>Balance</span>

                <strong className="text-[#B8E14A]">
                  ₹{enquiry.quotation.balance_amount}
                </strong>
              </div>

            </div>

            {enquiry.quotation.notes && (
              <div className="mt-4 text-sm text-[#B7D8D5]">
                <strong>Notes:</strong>{" "}
                {enquiry.quotation.notes}
              </div>
            )}

          </div>

        ) : (

          <div className="text-sm text-[#B7D8D5]">
            No quotation has been created yet.
          </div>

        )}

      </div>

    </div>
  );
}
/* =========================================================
   FLAVOURS
========================================================= */

function FlavoursTab() {
  const [flavours, setFlavours] = useState([]);

  const [form, setForm] = useState({
    name: "",
    price: 150,
    calories: 200,
    description: "",
    image: "",
    tags: "premium,veg",
  });

  const refresh = async () => {
    try {
      const response =
        await api.get("/flavours");

      setFlavours(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to load flavours");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const add = async () => {
    try {
      await api.post("/flavours", {
        ...form,
        tags: form.tags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        price: parseFloat(form.price),
        calories: parseInt(form.calories),
      });

      toast.success("Flavour added");

      setForm({
        name: "",
        price: 150,
        calories: 200,
        description: "",
        image: "",
        tags: "premium,veg",
      });

      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add flavour");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(
        `/flavours/${id}`
      );

      toast.success("Flavour deleted");
      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete flavour");
    }
  };

  return (
    <div>

      <div className="glass rounded-3xl p-6 mb-6">

        <h3 className="font-heading text-2xl mb-4">
          Add New Flavour
        </h3>

        <div className="grid md:grid-cols-3 gap-3">

          <input
            placeholder="Name"
            data-testid="new-flav-name"
            value={form.name}
            onChange={(e) =>
              setForm({
                ...form,
                name: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="Price"
            type="number"
            value={form.price}
            onChange={(e) =>
              setForm({
                ...form,
                price: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="Calories"
            type="number"
            value={form.calories}
            onChange={(e) =>
              setForm({
                ...form,
                calories: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-[#B7D8D5] block mb-1">
              Flavour Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];

                if (!file) return;

                try {
                  toast.loading("Uploading image...", {
                    id: "flavour-upload",
                  });

                  const url =
                    await uploadImageToCloudinary(file);

                  setForm((previous) => ({
                    ...previous,
                    image: url,
                  }));

                  toast.success("Image uploaded", {
                    id: "flavour-upload",
                  });
                } catch (error) {
                  toast.error("Image upload failed", {
                    id: "flavour-upload",
                  });
                }
              }}
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
            />
            {form.image && (
              <img
                src={form.image}
                alt="Flavour preview"
                className="mt-2 w-24 h-24 rounded-xl object-cover"
              />
            )}
          </div>

          <input
            placeholder="Tags (comma sep)"
            value={form.tags}
            onChange={(e) =>
              setForm({
                ...form,
                tags: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="Description"
            value={form.description}
            onChange={(e) =>
              setForm({
                ...form,
                description: e.target.value,
              })
            }
            className="md:col-span-3 rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

        </div>

        <button
          data-testid="new-flav-add"
          onClick={add}
          className="btn-primary mt-4"
        >
          <Plus className="w-4 h-4" />
          Add Flavour
        </button>

      </div>

      <div className="grid md:grid-cols-3 gap-4">

        {flavours.map((flavour) => (
          <FlavourEditCard
            key={flavour.id}
            f={flavour}
            onDelete={() =>
              del(flavour.id)
            }
            onUpdate={refresh}
          />
        ))}

      </div>

    </div>
  );
}


/* =========================================================
   FLAVOUR EDIT CARD
========================================================= */

function FlavourEditCard({
  f,
  onDelete,
  onUpdate,
}) {
  const [editing, setEditing] =
    useState(false);

  const [price, setPrice] =
    useState(f.price);

  const [img, setImg] =
    useState(f.image || "");

  const uploadImg = async (file) => {
    if (!file) return;

    try {
      toast.loading("Uploading image...", {
        id: `flavour-edit-${f.id}`,
      });

      const url =
        await uploadImageToCloudinary(file);

      setImg(url);

      toast.success("Image uploaded", {
        id: `flavour-edit-${f.id}`,
      });
    } catch (error) {
      console.error(error);

      toast.error("Image upload failed", {
        id: `flavour-edit-${f.id}`,
      });
    }
  };
  const save = async () => {
    try {
      await api.patch(
        `/flavours/${f.id}`,
        {
          image: img,
          price: parseFloat(price),
        }
      );

      toast.success("Updated");

      setEditing(false);
      onUpdate();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update flavour");
    }
  };

  return (
    <div className="glass rounded-2xl p-4">

      <div className="flex gap-3">

        {img ? (
          <img
            src={img}
            alt=""
            className="w-16 h-16 rounded-xl object-cover"
          />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-white/30 flex items-center justify-center text-xs">
            No image
          </div>
        )}

        <div className="flex-1 min-w-0">

          <div className="font-medium truncate">
            {f.name}
          </div>

          <div className="text-xs text-[#B7D8D5]">
            ₹{f.price} ·{" "}
            {f.calories ?? 0}cal
          </div>

        </div>

        <button
          onClick={onDelete}
          className="text-red-500 self-start"
          data-testid={`del-flav-${f.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>

      </div>

      {editing ? (
        <div className="mt-3 space-y-2">

          <input
            type="number"
            value={price}
            onChange={(e) =>
              setPrice(e.target.value)
            }
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-3 py-1.5 text-sm text-[#1F5F5C] bg-white"
            placeholder="Price"
          />

          <input
            placeholder="Image URL"
            value={
              img.startsWith("data:")
                ? ""
                : img
            }
            onChange={(e) =>
              setImg(e.target.value)
            }
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-3 py-1.5 text-sm text-[#1F5F5C] bg-white"
          />

          <label className="text-xs cursor-pointer text-[#B8E14A] hover:underline block">

            or upload file

            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) =>
                e.target.files?.[0] &&
                uploadImg(
                  e.target.files[0]
                )
              }
            />

          </label>

          <div className="flex gap-2">

            <button
              onClick={save}
              className="text-xs px-3 py-1 rounded-full bg-[#B8E14A] text-[#1F5F5C]"
            >
              Save
            </button>

            <button
              onClick={() =>
                setEditing(false)
              }
              className="text-xs px-3 py-1 rounded-full border"
            >
              Cancel
            </button>

          </div>

        </div>
      ) : (
        <button
          onClick={() =>
            setEditing(true)
          }
          data-testid={`edit-flav-${f.id}`}
          className="mt-3 text-xs text-[#B8E14A] hover:underline"
        >
          Edit image & price
        </button>
      )}

    </div>
  );
}


/* =========================================================
   MENU
========================================================= */

function MenuTab() {
  const [items, setItems] =
    useState([]);

  const [form, setForm] =
    useState({
      name: "",
      category: "Regular Shakes",
      price: 180,
      description: "",
      image: "",
    });

  const refresh = async () => {
    try {
      const response =
        await api.get("/menu-items");

      setItems(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to load menu items");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const add = async () => {
    try {
      await api.post(
        "/menu-items",
        {
          ...form,
          price: parseFloat(
            form.price
          ),
          tags: ["veg"],
        }
      );

      toast.success("Menu item added");

      setForm({
        name: "",
        category: "Regular Shakes",
        price: 180,
        description: "",
        image: "",
      });

      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add menu item");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(
        `/menu-items/${id}`
      );

      toast.success("Menu item deleted");
      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete menu item");
    }
  };

  return (
    <div>

      <div className="glass rounded-3xl p-6 mb-6">

        <h3 className="font-heading text-2xl mb-4">
          Add Menu Item
        </h3>

        <div className="grid md:grid-cols-3 gap-3">

          <input
            placeholder="Name"
            value={form.name}
            onChange={(e) =>
              setForm({
                ...form,
                name: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="Category"
            value={form.category}
            onChange={(e) =>
              setForm({
                ...form,
                category: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="Price"
            type="number"
            value={form.price}
            onChange={(e) =>
              setForm({
                ...form,
                price: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-[#B7D8D5] block mb-1">
              Menu Item Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];

                if (!file) return;

                try {
                  toast.loading("Uploading image...", {
                    id: "menu-upload",
                  });

                  const url =
                    await uploadImageToCloudinary(file);

                  setForm((previous) => ({
                    ...previous,
                    image: url,
                  }));

                  toast.success("Image uploaded", {
                    id: "menu-upload",
                  });
                } catch (error) {
                  toast.error("Image upload failed", {
                    id: "menu-upload",
                  });
                }
              }}
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
            />
            {form.image && (
              <img
                src={form.image}
                alt="Menu item preview"
                className="mt-2 w-24 h-24 rounded-xl object-cover"
              />
            )}
          </div>

          <input
            placeholder="Description"
            value={form.description}
            onChange={(e) =>
              setForm({
                ...form,
                description: e.target.value,
              })
            }
            className="md:col-span-3 rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

        </div>

        <button
          onClick={add}
          className="btn-primary mt-4"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </button>

      </div>

      <div className="grid md:grid-cols-3 gap-4">

        {items.map((item) => (
          <MenuEditCard
            key={item.id}
            m={item}
            onDelete={() =>
              del(item.id)
            }
            onUpdate={refresh}
          />
        ))}

      </div>

    </div>
  );
}


/* =========================================================
   MENU EDIT CARD
========================================================= */

function MenuEditCard({
  m,
  onDelete,
  onUpdate,
}) {
  const [editing, setEditing] =
    useState(false);

  const [price, setPrice] =
    useState(m.price);

  const [img, setImg] =
    useState(m.image || "");

  const uploadImg = async (file) => {
    if (!file) return;

    try {
      toast.loading("Uploading image...", {
        id: `menu-edit-${m.id}`,
      });

      const url =
        await uploadImageToCloudinary(file);

      setImg(url);

      toast.success("Image uploaded", {
        id: `menu-edit-${m.id}`,
      });
    } catch (error) {
      console.error(error);

      toast.error("Image upload failed", {
        id: `menu-edit-${m.id}`,
      });
    }
  };

  const save = async () => {
    try {
      await api.patch(
        `/menu-items/${m.id}`,
        {
          image: img,
          price: parseFloat(price),
        }
      );

      toast.success("Updated");

      setEditing(false);
      onUpdate();
    } catch (error) {
      console.error(error);
      toast.error(
        "Failed to update menu item"
      );
    }
  };

  return (
    <div className="glass rounded-2xl p-4">

      <div className="flex gap-3">

        {img ? (
          <img
            src={img}
            alt=""
            className="w-16 h-16 rounded-xl object-cover"
          />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-white/30 flex items-center justify-center text-xs">
            No image
          </div>
        )}

        <div className="flex-1 min-w-0">

          <div className="font-medium truncate">
            {m.name}
          </div>

          <div className="text-xs text-[#B7D8D5]">
            {m.category} · ₹{m.price}
          </div>

        </div>

        <button
          onClick={onDelete}
          className="text-red-500"
          data-testid={`del-menu-${m.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>

      </div>

      {editing ? (
        <div className="mt-3 space-y-2">

          <input
            type="number"
            value={price}
            onChange={(e) =>
              setPrice(e.target.value)
            }
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-3 py-1.5 text-sm text-[#1F5F5C] bg-white"
            placeholder="Price"
          />

          <input
            placeholder="Image URL"
            value={
              img.startsWith("data:")
                ? ""
                : img
            }
            onChange={(e) =>
              setImg(e.target.value)
            }
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-3 py-1.5 text-sm text-[#1F5F5C] bg-white"
          />

          <label className="text-xs cursor-pointer text-[#B8E14A] hover:underline block">

            or upload file

            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) =>
                e.target.files?.[0] &&
                uploadImg(
                  e.target.files[0]
                )
              }
            />

          </label>

          <div className="flex gap-2">

            <button
              onClick={save}
              className="text-xs px-3 py-1 rounded-full bg-[#B8E14A] text-[#1F5F5C]"
            >
              Save
            </button>

            <button
              onClick={() =>
                setEditing(false)
              }
              className="text-xs px-3 py-1 rounded-full border"
            >
              Cancel
            </button>

          </div>

        </div>
      ) : (
        <button
          onClick={() =>
            setEditing(true)
          }
          data-testid={`edit-menu-${m.id}`}
          className="mt-3 text-xs text-[#B8E14A] hover:underline"
        >
          Edit image & price
        </button>
      )}

    </div>
  );
}


/* =========================================================
   COUPONS
========================================================= */

function CouponsTab() {
  const [list, setList] =
    useState([]);

  const [form, setForm] =
    useState({
      code: "",
      discount_percent: 10,
      min_order: 0,
      description: "",
    });

  const refresh = async () => {
    try {
      const response =
        await api.get("/coupons");

      setList(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to load coupons");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const add = async () => {
    try {
      await api.post(
        "/coupons",
        {
          ...form,
          code:
            form.code.toUpperCase(),
          discount_percent:
            parseFloat(
              form.discount_percent
            ),
          min_order:
            parseFloat(
              form.min_order
            ),
        }
      );

      toast.success("Coupon added");

      setForm({
        code: "",
        discount_percent: 10,
        min_order: 0,
        description: "",
      });

      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add coupon");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(
        `/coupons/${id}`
      );

      toast.success("Coupon deleted");
      refresh();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete coupon");
    }
  };

  return (
    <div>

      <div className="glass rounded-3xl p-6 mb-6">

        <h3 className="font-heading text-2xl mb-4">
          Add Coupon
        </h3>

        <div className="grid md:grid-cols-4 gap-3">

          <input
            placeholder="CODE"
            value={form.code}
            onChange={(e) =>
              setForm({
                ...form,
                code: e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="% off"
            type="number"
            value={
              form.discount_percent
            }
            onChange={(e) =>
              setForm({
                ...form,
                discount_percent:
                  e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="Min order"
            type="number"
            value={form.min_order}
            onChange={(e) =>
              setForm({
                ...form,
                min_order:
                  e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

          <input
            placeholder="Description"
            value={form.description}
            onChange={(e) =>
              setForm({
                ...form,
                description:
                  e.target.value,
              })
            }
            className="rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-2 text-[#1F5F5C] bg-white"
          />

        </div>

        <button
          onClick={add}
          className="btn-primary mt-4"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>

      </div>

      <div className="grid md:grid-cols-3 gap-4">

        {list.map((coupon) => (
          <div
            key={coupon.id}
            className="glass rounded-2xl p-4"
          >

            <div className="flex justify-between">

              <div>

                <div className="font-heading text-xl text-[#B8E14A]">
                  {coupon.code}
                </div>

                <div className="text-xs text-[#B7D8D5]">
                  {coupon.discount_percent}%
                  {" "}off · Min ₹
                  {coupon.min_order}
                </div>

                <div className="text-xs mt-1">
                  {coupon.description}
                </div>

              </div>

              <button
                onClick={() =>
                  del(coupon.id)
                }
                className="text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>

            </div>

          </div>
        ))}

      </div>

    </div>
  );
}
function FeedbackTab({ feedback = [] }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#B8E14A]">
          Customer Feedback
        </div>

        <h2 className="font-heading text-3xl mt-1">
          Feedback & Complaints
        </h2>

        <p className="text-white/60 text-sm mt-2">
          Private feedback submitted by customers.
        </p>
      </div>

      {feedback.length === 0 ? (
        <div className="glass rounded-3xl p-8 text-center">
          <p className="text-white/60">
            No feedback submitted yet.
          </p>
        </div>
      ) : (
        <div className="grid gap-5">
          {feedback.map((item) => (
            <div
              key={item._id || item.id}
              className="glass rounded-3xl p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-heading text-xl">
                    {item.name}
                  </h3>

                  <p className="text-sm text-white/60 mt-1">
                    📞 {item.phone}
                  </p>
                </div>

                <div className="text-[#B8E14A] text-lg">
                  {"★".repeat(Number(item.rating || 0))}
                  <span className="text-white/20">
                    {"★".repeat(
                      5 - Number(item.rating || 0)
                    )}
                  </span>
                </div>
              </div>

              <div className="mt-5 rounded-2xl bg-white/5 p-4">
                <p className="text-white/80 leading-relaxed">
                  {item.message}
                </p>
              </div>

              {item.created_at && (
                <p className="text-xs text-white/40 mt-4">
                  {new Date(item.created_at).toLocaleString()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   FOUNDER
========================================================= */

function FounderTab() {
  const [founder, setFounder] =
    useState(null);

  const [saving, setSaving] =
    useState(false);

  useEffect(() => {
    api
      .get("/founder")
      .then((response) =>
        setFounder(response.data)
      )
      .catch((error) => {
        console.error(error);
        toast.error(
          "Failed to load founder details"
        );
      });
  }, []);

  const uploadFounderImage = async (file) => {
    if (!file) return;

    try {
      toast.loading("Uploading founder image...", {
        id: "founder-upload",
      });

      const url = await uploadImageToCloudinary(file);

      const updatedFounder = {
        ...founder,
        image: url,
      };

      const { id, ...patch } = updatedFounder;

      const response = await api.patch(
        "/founder",
        patch
      );

      setFounder(response.data);

      toast.success(
        "Founder image updated successfully",
        {
          id: "founder-upload",
        }
      );
    } catch (error) {
      console.error(
        "Founder image update failed:",
        error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Founder image update failed",
        {
          id: "founder-upload",
        }
      );
    }
  };



  const save = async () => {
    if (!founder) return;

    try {
      setSaving(true);

      const {
        id,
        ...patch
      } = founder;

      const response =
        await api.patch(
          "/founder",
          patch
        );

      setFounder(response.data);

      toast.success(
        "Founder details saved successfully"
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.detail ||
          "Failed to save founder details"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!founder) {
    return (
      <div className="text-[#B7D8D5]">
        Loading Founder details...
      </div>
    );
  }

  return (
    <div className="space-y-6">

      <div>

        <div className="text-xs uppercase tracking-widest text-[#B8E14A]">
          Founder Management
        </div>

        <h2 className="font-heading text-3xl mt-1">
          Edit Founder Profile
        </h2>

        <p className="text-sm text-[#B7D8D5] mt-2">
          Update the information displayed
          on the public Founder page.
        </p>

      </div>

      <div className="glass rounded-3xl p-6 md:p-8">

        {/* IMAGE */}

        <div className="mb-8">

          <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Founder Image
          </label>

          <div className="mt-3 flex flex-col sm:flex-row gap-6 items-start">

            {founder.image && (
              <img
                src={founder.image}
                alt="Founder"
                className="w-40 h-48 rounded-2xl object-cover shadow-xl"
              />
            )}

            <div>

              <label className="inline-flex cursor-pointer btn-outline text-sm">

                Upload New Image

                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) =>
                    uploadFounderImage(
                      e.target.files?.[0]
                    )
                  }
                />

              </label>

              <p className="text-xs text-[#B7D8D5] mt-2">
                JPG, PNG or WEBP image recommended.
              </p>

            </div>

          </div>

        </div>

        {/* BASIC DETAILS */}

        <div className="grid md:grid-cols-2 gap-4">

          <div>

            <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Founder Name
            </label>

            <input
              value={
                founder.name || ""
              }
              onChange={(e) =>
                setFounder({
                  ...founder,
                  name: e.target.value,
                })
              }
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
            />

          </div>

          <div>

            <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Designation
            </label>

            <input
              value={
                founder.designation ||
                ""
              }
              onChange={(e) =>
                setFounder({
                  ...founder,
                  designation:
                    e.target.value,
                })
              }
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
            />

          </div>

        </div>

        {/* DESCRIPTION 1 */}

        <div className="mt-4">

          <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Description
          </label>

          <textarea
            rows={5}
            value={
              founder.description1 ||
              ""
            }
            onChange={(e) =>
              setFounder({
                ...founder,
                description1:
                  e.target.value,
              })
            }
            className="w-full rounded-2xl border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white resize-none"
          />

        </div>

        {/* DESCRIPTION 2 */}

        <div className="mt-4">

          <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Second Description
          </label>

          <textarea
            rows={5}
            value={
              founder.description2 ||
              ""
            }
            onChange={(e) =>
              setFounder({
                ...founder,
                description2:
                  e.target.value,
              })
            }
            className="w-full rounded-2xl border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white resize-none"
          />

        </div>

        {/* LOCATION */}

        <div className="mt-4">

          <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
            Location
          </label>

          <input
            value={
              founder.location || ""
            }
            onChange={(e) =>
              setFounder({
                ...founder,
                location:
                  e.target.value,
              })
            }
            className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
          />

        </div>

        {/* STATS */}

        <div className="grid md:grid-cols-3 gap-4 mt-6">

          <div>

            <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Signature Flavours
            </label>

            <input
              value={
                founder.signature_flavours ||
                ""
              }
              onChange={(e) =>
                setFounder({
                  ...founder,
                  signature_flavours:
                    e.target.value,
                })
              }
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
            />

          </div>

          <div>

            <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Natural Ingredients
            </label>

            <input
              value={
                founder.natural_ingredients ||
                ""
              }
              onChange={(e) =>
                setFounder({
                  ...founder,
                  natural_ingredients:
                    e.target.value,
                })
              }
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
            />

          </div>

          <div>

            <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Handcrafted Fresh
            </label>

            <input
              value={
                founder.handcrafted_fresh ||
                ""
              }
              onChange={(e) =>
                setFounder({
                  ...founder,
                  handcrafted_fresh:
                    e.target.value,
                })
              }
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
            />

          </div>

        </div>

        {/* CTA */}

        <div className="grid md:grid-cols-2 gap-4 mt-6">

          <div>

            <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Button Text
            </label>

            <input
              value={
                founder.cta_text || ""
              }
              onChange={(e) =>
                setFounder({
                  ...founder,
                  cta_text:
                    e.target.value,
                })
              }
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
            />

          </div>

          <div>

            <label className="text-xs uppercase tracking-widest text-[#B7D8D5]">
              Button Link
            </label>

            <input
              value={
                founder.cta_link || ""
              }
              onChange={(e) =>
                setFounder({
                  ...founder,
                  cta_link:
                    e.target.value,
                })
              }
              className="w-full rounded-full border border-[rgba(184,225,74,0.35)] px-4 py-3 mt-1 text-[#1F5F5C] bg-white"
            />

          </div>

        </div>

        {/* SAVE */}

        <div className="mt-8 pt-6 border-t border-white/10">

          <button
            onClick={save}
            disabled={saving}
            className="btn-primary"
          >
            {saving
              ? "Saving..."
              : "Save Founder Changes"}
          </button>

        </div>

      </div>
    </div>
  );
}